import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { LogIn, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import PageBlocks from './PageBlocks';
import { useTranslation } from '../i18nContext';
import { buildLocalePath, isValidLocale } from '../i18n';
import { usePublicConfig } from '../lib/publicConfig';
import { useAuth } from '../lib/AuthContext';
import logo from '../assets/logo.png';

export default function Footer() {
  const { lang } = useParams();
  const t = useTranslation();
  const locale = lang && isValidLocale(lang) ? lang : 'es';
  const publicConfig = usePublicConfig();
  const { user } = useAuth();
  // Hide site navigation columns for standard members while the site is in
  // maintenance mode — they can only access their own profile.
  const hideInternal = !!(publicConfig && publicConfig.maintenance_mode) && !!user && user.role === 'interpreter';
  const social = publicConfig?.social || {};
  const socialLinks = [
    { url: social.facebook, Icon: Facebook, label: 'Facebook' },
    { url: social.instagram, Icon: Instagram, label: 'Instagram' },
    { url: social.youtube, Icon: Youtube, label: 'YouTube' },
    { url: social.linkedin, Icon: Linkedin, label: 'LinkedIn' },
  ].filter((s) => s.url);

  const institutionalLinks = [
    { label: t.nav.about, to: buildLocalePath(locale, 'about') },
    { label: t.nav.membership, to: buildLocalePath(locale, 'membership') },
    { label: t.nav.resources, to: buildLocalePath(locale, 'resources') },
    { label: t.nav.contact, to: buildLocalePath(locale, 'contact') },
  ];

  const communityLinks = [
    { label: t.nav.directory, to: buildLocalePath(locale, 'directory') },
    { label: t.nav.news, to: buildLocalePath(locale, 'news') },
    { label: t.nav.events, to: buildLocalePath(locale, 'events') },
  ];

  return (
    <footer className="bg-charcoal-800 text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <Link to={buildLocalePath(locale, 'home')}>
              <img src={logo} alt="CMIC" className="h-12 w-auto brightness-0 invert mb-4" />
            </Link>
            <p className="text-charcoal-300 text-sm leading-relaxed max-w-md mb-6">{t.footer.description}</p>
            <p className="text-sm text-cmic-300 italic">{t.footer.motto}</p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3 mt-6" data-testid="footer-social">
                {socialLinks.map(({ url, Icon, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="w-10 h-10 rounded-full bg-charcoal-700 hover:bg-cmic-600 text-charcoal-200 hover:text-white flex items-center justify-center transition-colors"
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">{t.footer.institutional}</h4>
            <ul className="space-y-3">
              {!hideInternal && institutionalLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-charcoal-300 hover:text-cmic-300 text-sm transition-colors duration-200">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">{t.footer.community}</h4>
            <ul className="space-y-3">
              {!hideInternal && communityLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-charcoal-300 hover:text-cmic-300 text-sm transition-colors duration-200">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-charcoal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-charcoal-400">© {t.footer.copyright}</p>
            <p className="text-[11px] text-charcoal-600" data-testid="footer-credit">
              {locale === 'es' ? 'Sitio desarrollado por' : 'Site developed by'}{' '}
              <span className="text-charcoal-400">Álvaro Diego Fernández</span>
            </p>
          </div>
          <div className="flex items-center gap-5 flex-wrap justify-center">
            <Link
              to={buildLocalePath(locale, 'privacy')}
              data-testid="footer-privacy-link"
              className="text-xs text-charcoal-400 hover:text-cmic-300 transition-colors"
            >
              {locale === 'es' ? 'Aviso de privacidad' : 'Privacy Notice'}
            </Link>
            <p className="text-xs text-charcoal-500">{t.footer.fitMember}</p>
            <Link
              to="/login"
              data-testid="footer-member-access-btn"
              className="inline-flex items-center gap-2 text-xs font-medium text-charcoal-400 hover:text-cmic-300 border border-charcoal-600 hover:border-cmic-400 rounded-lg px-3.5 py-2 transition-all duration-200"
            >
              <LogIn size={14} />
              {t.footer.memberAccess}
            </Link>
          </div>
        </div>
        <PageBlocks pageKey="footer" />
      </div>
    </footer>
  );
}
